import { useEffect, useState } from "react";
import {
	Box,
	Button,
	Flex,
	Icon,
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalFooter,
	ModalOverlay,
	Stack,
	Text,
	useToast
} from "@chakra-ui/react";
import {
	RiArrowLeftLine,
	RiDeleteBinLine,
	RiEdit2Line,
	RiMap2Line,
	RiMapPinLine,
	RiSearchLine,
	RiTreasureMapLine
} from "react-icons/ri";
import { SubmitHandler, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import SearchGoogle from "../SearchGoogle";
import { geocodeByPlaceId, getLatLng } from "react-google-places-autocomplete";
import { v4 as uuid } from 'uuid';

import api from "../../services/api";
import { useAuth } from '../../hooks/auth';
import { useCart } from "../../hooks/cart";
import { Input } from "../Inputs/Input";
import { FaGoogle } from "react-icons/fa";

type EnderecoFormData = {
  cep: string;
  uf: string;
  cid: string;
  cidcod: string;
  bai: string;
  endereco: string;
  num: string;
  comp?: string;
  ponto_ref?: string;
}

type EnderecoData = {
	id: string;
  cep: string;
  uf: string;
  cid: string;
  cidcod: string;
  bai: string;
  endereco: string;
  num: string;
  comp?: string;
  ponto_ref?: string;
	emp_cod: string;
	formatted_address: string;
	location: {
		lat: number;
		lng: number;
	};
}
interface ModalEnderecoProps {
	isOpen: boolean;
	onRequestClose: (close: boolean) => void;
	retirar: boolean;
	enderecos: EnderecoData[];
	setEnderecos: (enderecos: EnderecoData[]) => void;
}

const enderecoFormSchema: yup.SchemaOf<EnderecoFormData> = yup.object().shape({
	cep: yup.string().required('Cep obrigatório.'),
	uf: yup.string().required('Estado obrigatório.'),
	cid: yup.string().required('Cidade obrigatório.'),
	cidcod: yup.string().required(),
	bai: yup.string().required('Bairro obrigatório.'),
	endereco: yup.string().required('Endereço obrigatório.'),
	num: yup.string().required('Número obrigatório.'),
	comp: yup.string(),
	ponto_ref: yup.string()
});

const ModalEndereco: React.FC<ModalEnderecoProps> = ({
	isOpen,
	onRequestClose,
	retirar,
	enderecos,
	setEnderecos,
}) => {
	const { company, token } = useAuth();
	const { cart, addCartEndereco } = useCart();
	const toast = useToast();

	const [cadEndereco, setCadEndereco] = useState(false);
	const [auxEndereco, setAuxEndereco] = useState<EnderecoData>({} as EnderecoData);

  const { register, handleSubmit, formState, setValue, reset, setFocus } = useForm<EnderecoFormData>({
		resolver: yupResolver(enderecoFormSchema)
	});

	function handleMasktCep(event: string) {
		let res = event;
		res = res.replace(/[A-Za-z]/g, '');
		setValue('cep', res);
	};

	async function handleCep(value: string, isGoogleSearch: boolean) {
		let cep = value;
		// Verifica se campo cep possui valor informado
		if (cep !== '' || cep !== undefined || cep !== null) {
			cep = cep.replace(/\D/g, '');
			// Expressão regular para validar o CEP.
			const validacep = /^[0-9]{8}$/;
			// Valida o formato do CEP.
			if (validacep.test(cep)) {
				delete api.defaults.headers.common['Authorization'];
				delete api.defaults.headers.common['company'];

				await api
					.get(`https://viacep.com.br/ws/${cep}/json`)
					.then(response => {
						if (!isGoogleSearch) {
							setValue('cid', response.data.localidade);
							setValue('bai', response.data.bairro);
							setValue(
								'endereco',
								response.data.logradouro,
							);
							setValue('uf', response.data.uf);
							setValue('comp', response.data.complemento);

							setFocus('num');
						}

						setValue('cidcod', response.data.ibge);
					})
					.catch(err => {
						toast({
							title: 'Erro no Cep.',
							description: `Error - ${err}.`,
							status: 'error',
							duration: 5000,
							position: 'top-right',
							isClosable: true,
						});
					});

				api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
				api.defaults.headers.common['company'] = company.id;
			} /* else {
				toast({
					title: 'Erro no Cep.',
					description: `CEP digitado é inválido.`,
					status: 'error',
					duration: 5000,
					position: 'top-right',
					isClosable: true,
				});
			} */
		}
	};

	async function onLocationSelected(data: any) {
		await geocodeByPlaceId(data.value.place_id)
			.then(results => {
				const end = results[0].address_components;
				handleCep(end.length === 7 ? end[6].short_name : end.length === 6 ? end[5].short_name : '', true);
				setValue('cep', end.length === 7 ? end[6].short_name : end.length === 6 ? end[5].short_name : '');
				setValue('uf', end.length === 7 ? end[4].short_name : end.length === 6 ? end[3].short_name : '');
				setValue('cid', end.length === 7 ? end[3].short_name : end.length === 6 ? end[2].short_name : '');
				setValue('bai', end.length === 7 ? end[2].short_name : end.length === 6 ? end[1].short_name : '');
				setValue('endereco', end.length === 7 ? end[1].short_name : end.length === 6 ? end[0].short_name : '');
				setValue('num', end.length === 7 ? end[0].short_name : '');
				setFocus(end.length === 7 ? 'comp' : 'num');

				return results;
			})
			.then(results => getLatLng(results[0]))
			.then(location => console.log(location))
			.catch(error => console.log(error))
	}

  const handleSubmitEndereco: SubmitHandler<EnderecoFormData> = async (data) => {
		if (!cart.consumidor) {
			if (auxEndereco.id) {
				const endereco = {
					...auxEndereco,
					cep: data.cep,
          uf: data.uf,
					cid: data.cid,
					cidcod: data.cidcod,
          bai: data.bai,
          endereco: data.endereco,
          num: data.num,
					comp: data.comp || "",
					ponto_ref: data.ponto_ref || "",
				};

				await api.put('controle/endereco', { endereco, cli_cod: cart.cliente.id })
					.then(response => {
						setEnderecos(response.data);
						reset({} as EnderecoData);
						setAuxEndereco({} as EnderecoData);
						setCadEndereco(!cadEndereco);

						if (cart.endereco_entrega.id === endereco.id) {
							addCartEndereco(endereco, retirar);
						}
					});
			} else {
				const endereco = { ...data, id: uuid(), emp_cod: company.id };

				await api.post('controle/endereco', { endereco, cli_cod: cart.cliente.id })
					.then(response => {
						setEnderecos(response.data);
						reset({} as EnderecoData);
						setAuxEndereco({} as EnderecoData);
						setCadEndereco(!cadEndereco);
					});
			}
		} else {
			const endereco = {
        ...data,
        id: uuid(),
        emp_cod: company.id,
        comp: data.comp ? data.comp : '',
        ponto_ref: data.ponto_ref ? data.ponto_ref : '',
        formatted_address: '',
        location: { lat: 0, lng: 0 }
      };
			setEnderecos([endereco]);

			addCartEndereco(endereco, false);
			reset({} as EnderecoData);
			setAuxEndereco({} as EnderecoData);
			setCadEndereco(!cadEndereco);
		}
	};

	async function handleRemoveEndereco(end_cod: string) {
		await api.delete(`controle/endereco/${end_cod}/${cart.cliente.id}`)
			.then(response => {
				setEnderecos(response.data);
			});
	}

	return (
		<Modal
			isCentered={true}
			size="xl"
			isOpen={isOpen}
			onClose={() => onRequestClose(false)}
		>
			<ModalOverlay />

			<ModalContent
				as="form"
				mt={cadEndereco ? "60" : "20"}
				onSubmit={handleSubmit(handleSubmitEndereco)}
			>
				<ModalCloseButton />

				<ModalBody mt="2">
					<Flex
						as="button"
						type="button"
						w="96%"
						bg="gray.50"
						py={2}
						px={4}
            mt={2}
						mb={4}
						align="center"
						borderRadius={20}
						onClick={() => {
							// cadEndereco && reset();
							reset({} as EnderecoData);
							setAuxEndereco({} as EnderecoData);
							setCadEndereco(!cadEndereco);
						}}
					>
						{cadEndereco ? (
							<>
								<Icon as={RiArrowLeftLine} fontSize="20" color="blue.500" mr="2" />
								<Text color="gray.500">Voltar</Text>
							</>
						) : (
								<>
									<Icon as={RiSearchLine} fontSize="20" color="blue.500" mr="2" />
									<Text color="gray.500">Buscar endereço</Text>
								</>
						)}
					</Flex>

					{/* ENDEREÇOS */}
					{!cadEndereco && (
						<Flex
							w="100%"
							flexDir="column"
						>
							{enderecos.length > 0 ? enderecos.map(endereco => {
								return (
									<Flex
										key={endereco.id}
										justify="space-between"
										align="center"
										p="4"
										m="2"
										borderWidth={1}
										borderColor={cart.endereco_entrega.id === endereco.id ? "blue.100" : "gray.200"}
										borderRadius={8}
									>
										<Flex
											align="center"
											cursor="pointer"
											w="100%"
											onClick={() => {
												addCartEndereco(endereco, retirar);
												onRequestClose(false);
											}}
										>
											<Icon as={RiMap2Line} fontSize="24" color="gray.400" mr="4" />

											<Flex flexDir="column">
												<Text color="gray.600">
													{endereco.endereco}
													{endereco.num && `, ${endereco.num}`}
												</Text>
												<Text fontSize={14} color="gray.400">
													{endereco.comp && `${endereco.comp}, `}
													{endereco.bai}
													{endereco.cid && `, ${endereco.cid}`}
													{endereco.uf && ` - ${endereco.uf}`}
												</Text>
											</Flex>
										</Flex>

										<Stack direction="row" spacing="2">
											<Flex
												as="button"
												align="center"
												justifyContent="center"
												fontSize={14}
												fontWeight="500"
												color="blue.100"
												onClick={() => {
													setAuxEndereco(endereco);
													reset(endereco);
													setCadEndereco(true);
												}}
											>
												<Icon as={RiEdit2Line} fontSize={24} />
											</Flex>

											{!cart.consumidor && (
												<Flex
													as="button"
													fontSize={14}
													fontWeight="500"
													color="red.500"
													onClick={() => handleRemoveEndereco(endereco.id)}
												>
													<Icon as={RiDeleteBinLine} fontSize={24} />
												</Flex>
											)}
										</Stack>
									</Flex>
								)
							}) : (
								<Flex flexDir="column" justify="center" align="center" p="4">
									<Flex justify="center" align="center" p="2" bg="gray.100" borderRadius="full">
											<Icon as={RiTreasureMapLine} fontSize={40} />
									</Flex>
									<Text fontWeight="500" mt="2">
											{cart.consumidor ? 'Adicione um endereço para o consumidor.' :
											'Nenhum Endereço Cadastrado para este cliente.'
											}
									</Text>
								</Flex>
							)}
						</Flex>
					)}

					{/* CADASTRO ENDEREÇO */}
					{cadEndereco && (
						<Stack>
							<Box mb="4">
								<SearchGoogle
									onLocationSelected={onLocationSelected}
									label="Buscar Endereço"
									icon={FaGoogle}
								/>
							</Box>

							<Input
								label="CEP"
								iconLabel={RiMapPinLine}
                bgColor="gray.50"
								{...register("cep")}
								error={formState.errors.cep}
								onChange={(e: any) => handleMasktCep(e.target.value)}
                onBlur={(e: any) => handleCep(e.target.value, false)}
							/>

							{/* CIDADE, ESTADO */}
							<Flex>
								<Input
									label="Cidade"
									iconLabel={RiMapPinLine}
                  bgColor="gray.50"
									{...register("cid")}
									error={formState.errors.cid}
								/>

								<Box ml="4">
									<Input
										label="Estado"
										iconLabel={RiMapPinLine}
                    bgColor="gray.50"
										{...register("uf")}
										error={formState.errors.uf}
									/>
								</Box>
							</Flex>

							{/* BAIRRO */}
							<Input
								label="Bairro"
								iconLabel={RiMapPinLine}
                bgColor="gray.50"
								{...register("bai")}
								error={formState.errors.bai}
							/>

							{/* ENDEREÇO */}
							<Input
								label="Endereço"
								iconLabel={RiMapPinLine}
                bgColor="gray.50"
								{...register("endereco")}
								error={formState.errors.endereco}
							/>

							{/* NÚMERO, COMPLEMENTO */}
							<Flex>
								<Box mr="4">
									<Input
										label="Número"
										iconLabel={RiMapPinLine}
                    bgColor="gray.50"
										{...register("num")}
										error={formState.errors.num}
									/>
								</Box>

								<Input
									label="Complemento"
									iconLabel={RiMapPinLine}
                  bgColor="gray.50"
									{...register("comp")}
									error={formState.errors.comp}
								/>
							</Flex>

							{/* PONTO DE REFERENCIA */}
							<Input
								label="Ponto de referência"
								iconLabel={RiMapPinLine}
                bgColor="gray.50"
								{...register("ponto_ref")}
								error={formState.errors.ponto_ref}
							/>

							{/* CÓDIGO DA CIDADE */}
							<Input
								label="Cidade Cod"
								bgColor="gray.50"
								{...register("cidcod")}
								error={formState.errors.cidcod}
								display="none"
								visibility="hidden"
							/>
						</Stack>
					)}
				</ModalBody>

				<ModalFooter pt="2">
					{cadEndereco && (
						<Button colorScheme="blue" type="submit">Salvar</Button>
					)}
					<Button
						colorScheme="blackAlpha"
						ml="4"
						onClick={() => {
							!cadEndereco && onRequestClose(false);
							setAuxEndereco({} as EnderecoData)
							setCadEndereco(false)
							reset();
						}}
					>
						Cancelar
					</Button>
				</ModalFooter>
			</ModalContent>
		</Modal>
	)
}

export default ModalEndereco;
